import { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import {
  ChevronDown,
  ListMusic,
  MoreHorizontal,
  Shuffle,
  SkipBack,
  Play,
  Pause,
  SkipForward,
  Repeat,
  Heart,
  Mic2,
  Share2,
  Cast,
  MessageCircle,
  Loader2,
} from 'lucide-react';
import { useSongsFeed } from '@/hooks/useFeed';
import { useLikePost } from '@/hooks/usePost';
import { formatCount, formatDuration } from '@/lib/formatters';
import CommentModal from '@/components/CommentModal';
import { getPostUser, getPostImage, getLikesCount } from '@/components/PostCard';
import type { Post } from '@/types';


export default function Music() {
  // Feed data
  const { data: feedData, isLoading: isFeedLoading } = useSongsFeed();
  const songs = feedData?.pages.flatMap((page) => page.posts) ?? [];
  const nowPlaying = songs[0] ?? null;
  const queue = songs.slice(1);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(35);
  const [currentTime, setCurrentTime] = useState(0);

  // UI state
  const [showQueue, setShowQueue] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [followedArtists, setFollowedArtists] = useState<Set<string>>(new Set());

  // Refs for GSAP targets
  const albumRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const queueRef = useRef<HTMLDivElement>(null);

  // Post hooks — operate on now playing track
  const nowPlayingUlid = nowPlaying?.id ?? undefined;
  const liked = nowPlaying?.postEngagement?.liked ?? false;

  const likeMutation = useLikePost();

  // ─── GSAP entry animation ────────────────────────────────────────────────────
  useEffect(() => {
    const tl = gsap.timeline();

    tl.fromTo(
      albumRef.current,
      { y: 40, scale: 0.96, opacity: 0 },
      { y: 0, scale: 1, opacity: 1, duration: 0.45, ease: 'power2.out' },
    )
      .fromTo(
        infoRef.current,
        { y: 12, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.35, ease: 'power2.out' },
        '-=0.25',
      )
      .fromTo(
        controlsRef.current?.children ?? [],
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.3, stagger: 0.06, ease: 'power2.out' },
        '-=0.2',
      );

    if (isPlaying) {
      gsap.to(albumRef.current, {
        scale: 1.01,
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }

    return () => {
      gsap.killTweensOf(albumRef.current);
    };
  }, [isPlaying]);

  // ─── Progress ticker ─────────────────────────────────────────────────────────
  useEffect(() => {
    const duration = nowPlaying?.duration ?? 0;
    const interval = setInterval(() => {
      if (isPlaying) {
        setProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + 0.1;
        });
        if (duration > 0) {
          setCurrentTime((prev) => {
            if (prev >= duration) return 0;
            return prev + 0.1;
          });
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, nowPlaying?.duration]);


  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      gsap.to(albumRef.current, {
        scale: 1.01,
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    } else {
      gsap.killTweensOf(albumRef.current);
      gsap.to(albumRef.current, { scale: 1, duration: 0.3 });
    }
  };

  const handleLike = useCallback(() => {
    if (!nowPlaying) return;
    const heartEl = document.getElementById('music-heart');
    if (heartEl && !liked) {
      gsap.fromTo(
        heartEl,
        { scale: 0.8 },
        { scale: 1.2, duration: 0.15, yoyo: true, repeat: 1, ease: 'power2.out' },
      );
    }
    likeMutation.mutate({ ulid: nowPlaying.id, liked });
  }, [nowPlaying, liked, likeMutation]);

  const handleFollowArtist = (artist: string) => {
    setFollowedArtists((prev) => {
      const next = new Set(prev);
      if (next.has(artist)) {
        next.delete(artist);
      } else {
        next.add(artist);
      }
      return next;
    });
  };

  const toggleQueue = () => {
    setShowQueue(!showQueue);
    if (!showQueue && queueRef.current) {
      gsap.fromTo(
        queueRef.current,
        { y: '100%' },
        { y: 0, duration: 0.4, ease: 'power2.out' },
      );
    }
  };

  const openComments = () => {
    setShowComments(true);
  };

  const closeComments = () => {
    setShowComments(false);
  };

  // ─── Derived display values ──────────────────────────────────────────────────
  const nowPlayingArt = nowPlaying ? getPostImage(nowPlaying) : undefined;
  const nowPlayingTitle = nowPlaying?.title ?? 'No tracks available';
  const nowPlayingArtist = nowPlaying?.artist ?? (nowPlaying ? getPostUser(nowPlaying).name : '');
  const nowPlayingDuration = nowPlaying?.duration ?? 0;
  const isFollowingCurrentArtist = followedArtists.has(nowPlayingArtist);
  const currentTimeDisplay = nowPlayingDuration > 0 ? formatDuration(currentTime) : '0:00';
  const totalDurationDisplay = nowPlayingDuration > 0 ? formatDuration(nowPlayingDuration) : '0:00';
  const likesCount = nowPlaying ? getLikesCount(nowPlaying) : 0;

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (isFeedLoading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#A38BFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D10] relative overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(163, 139, 255, 0.3) 0%, transparent 70%)',
        }}
      />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 py-4 pt-safe">
        <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
          <ChevronDown size={22} />
        </button>
        <div className="text-center">
          <p className="text-xs text-[#A7B0C8] uppercase tracking-wider">Now Playing</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleQueue}
            className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]"
          >
            <ListMusic size={20} />
          </button>
          <button className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]">
            <MoreHorizontal size={20} />
          </button>
        </div>
      </div>

      {/* Album art */}
      <div className="relative z-10 flex justify-center px-8 pt-4">
        <div ref={albumRef} className="relative" style={{ width: 'min(72vw, 380px)' }}>
          {nowPlayingArt ? (
            <img
              src={nowPlayingArt}
              alt={nowPlayingTitle}
              className="w-full aspect-square object-cover rounded-[18px] pulse-shadow"
            />
          ) : (
            <div className="w-full aspect-square rounded-[18px] bg-[#141821] flex items-center justify-center pulse-shadow">
              <Play size={64} className="text-[#A38BFF]" />
            </div>
          )}
        </div>
      </div>

      {/* Track info */}
      <div ref={infoRef} className="relative z-10 px-8 pt-8">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-[#F5F7FF] mb-1">{nowPlayingTitle}</h2>
            <p className="text-sm text-[#A7B0C8]">{nowPlayingArtist}</p>
            <div className="flex items-center gap-3 mt-3">
              {nowPlayingArtist && (
                <button
                  onClick={() => handleFollowArtist(nowPlayingArtist)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isFollowingCurrentArtist
                      ? 'bg-[#141821] text-[#A7B0C8] border border-white/[0.08]'
                      : 'bg-[#A38BFF] text-[#0B0D10]'
                  }`}
                >
                  {isFollowingCurrentArtist ? 'Following' : 'Follow Artist'}
                </button>
              )}
              <span className="text-xs text-[#A7B0C8]">SONG</span>
            </div>
          </div>
          <button
            id="music-heart"
            onClick={handleLike}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              liked ? 'bg-[#A38BFF]/20 text-[#A38BFF]' : 'bg-[#141821] text-[#A7B0C8]'
            }`}
          >
            <Heart size={20} fill={liked ? '#A38BFF' : 'none'} />
          </button>
        </div>
      </div>

      {/* Scrubber */}
      <div className="relative z-10 px-8 pt-6">
        <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#A38BFF] rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs text-[#A7B0C8]">{currentTimeDisplay}</span>
          <span className="text-xs text-[#A7B0C8]">{totalDurationDisplay}</span>
        </div>
      </div>

      {/* Controls */}
      <div ref={controlsRef} className="relative z-10 px-8 pt-6">
        <div className="flex items-center justify-between">
          <button className="w-10 h-10 rounded-full flex items-center justify-center text-[#A7B0C8]">
            <Shuffle size={20} />
          </button>

          <button className="w-12 h-12 rounded-full flex items-center justify-center text-[#F5F7FF]">
            <SkipBack size={28} fill="currentColor" />
          </button>

          <button
            onClick={handlePlayPause}
            className="w-[72px] h-[72px] rounded-full bg-[#F5F7FF] flex items-center justify-center text-[#0B0D10] transition-transform active:scale-95"
          >
            {isPlaying ? (
              <Pause size={32} fill="currentColor" />
            ) : (
              <Play size={32} fill="currentColor" className="ml-1" />
            )}
          </button>

          <button className="w-12 h-12 rounded-full flex items-center justify-center text-[#F5F7FF]">
            <SkipForward size={28} fill="currentColor" />
          </button>

          <button className="w-10 h-10 rounded-full flex items-center justify-center text-[#A7B0C8]">
            <Repeat size={20} />
          </button>
        </div>
      </div>

      {/* Action row */}
      <div className="relative z-10 px-8 pt-8">
        <div className="flex items-center justify-between">
          <button
            onClick={handleLike}
            className={`flex flex-col items-center gap-1 transition-colors ${liked ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
          >
            <Heart size={22} fill={liked ? '#A38BFF' : 'none'} />
            <span className="text-xs">{formatCount(likesCount)}</span>
          </button>
          <button
            onClick={openComments}
            className="flex flex-col items-center gap-1 text-[#A7B0C8]"
          >
            <MessageCircle size={22} />
            <span className="text-xs">{formatCount(nowPlaying?.commentCount ?? 0)}</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-[#A7B0C8]">
            <Mic2 size={22} />
            <span className="text-xs">Lyrics</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-[#A7B0C8]">
            <Share2 size={22} />
            <span className="text-xs">Share</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-[#A7B0C8]">
            <Cast size={22} />
            <span className="text-xs">Devices</span>
          </button>
        </div>
      </div>

      {/* Queue Panel */}
      {showQueue && (
        <div
          ref={queueRef}
          className="fixed inset-x-0 bottom-0 bg-[#141821] rounded-t-[24px] z-50 max-h-[70vh]"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#F5F7FF]">Up Next</h3>
              <button
                onClick={toggleQueue}
                className="w-8 h-8 rounded-full bg-[#0B0D10] flex items-center justify-center text-[#A7B0C8]"
              >
                <ChevronDown size={18} />
              </button>
            </div>

            {queue.length === 0 ? (
              <div className="text-center py-8">
                <ListMusic size={40} className="text-[#2A2F3A] mx-auto mb-3" />
                <p className="text-[#A7B0C8] text-sm">No more tracks in queue</p>
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto max-h-[55vh]">
                {queue.map((track, index) => {
                  const trackArt = getPostImage(track);
                  const trackArtist = track.artist ?? getPostUser(track).name;
                  const isFollowingTrackArtist = followedArtists.has(trackArtist);

                  return (
                    <div
                      key={track.id}
                      className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#0B0D10] transition-colors"
                    >
                      <span className="text-sm text-[#A7B0C8] w-6">{index + 1}</span>
                      {trackArt ? (
                        <img
                          src={trackArt}
                          alt={track.title ?? ''}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#A38BFF]/20 flex items-center justify-center">
                          <Play size={16} className="text-[#A38BFF]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#F5F7FF] truncate">
                          {track.title ?? 'Untitled'}
                        </p>
                        <p className="text-xs text-[#A7B0C8] truncate">{trackArtist}</p>
                      </div>
                      {trackArtist && (
                        <button
                          onClick={() => handleFollowArtist(trackArtist)}
                          className={`text-[10px] px-2 py-1 rounded-full transition-colors flex-shrink-0 ${
                            isFollowingTrackArtist
                              ? 'bg-white/20 text-white'
                              : 'bg-[#A38BFF] text-[#0B0D10]'
                          }`}
                        >
                          {isFollowingTrackArtist ? 'Following' : 'Follow'}
                        </button>
                      )}
                      {track.duration != null && (
                        <span className="text-xs text-[#A7B0C8] flex-shrink-0">
                          {formatDuration(track.duration)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && nowPlayingUlid && (
        <CommentModal
          postUlid={nowPlayingUlid}
          onClose={closeComments}
        />
      )}
    </div>
  );
}
